import React, { useState, useEffect, useMemo } from 'react';
import { getScreeningQuestionByID, updateScreeningQuestion } from '../../../../controllers/ScreeningQuestionController';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import ButtonPrimary from '../../../../components/button/ButtonPrimary';
import ButtonSecondary from '../../../../components/button/ButtonSecondary';
import Skeleton from '../../../../components/Skeleton';

const EditModal = ({ t }) => {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [type, setType] = useState('');
    const [sequence, setSequence] = useState('');
    const [optionAID, setOptionAID] = useState('');
    const [optionBID, setOptionBID] = useState('');
    const [optionCID, setOptionCID] = useState('');
    const [optionDID, setOptionDID] = useState('');
    const [optionAEN, setOptionAEN] = useState('');
    const [optionBEN, setOptionBEN] = useState('');
    const [optionCEN, setOptionCEN] = useState('');
    const [optionDEN, setOptionDEN] = useState('');
    const navigate = useNavigate();
    
    useEffect(() => {
        const fetchScreeningQuestion = async () => {
            try {
                const result = await getScreeningQuestionByID(id);
                if (!result) {
                    throw new Error(t('screeningQuestion.fetchData.errorMessage'));
                };
                const { type, sequence, options } = result;
                setType(type);
                setSequence(sequence);
                setOptionAID(options[0].a);
                setOptionBID(options[0].b);
                setOptionCID(options[0].c);
                setOptionDID(options[0].d);
                setOptionAEN(options[1].a);
                setOptionBEN(options[1].b);
                setOptionCEN(options[1].c);
                setOptionDEN(options[1].d);
            } catch (error) {
                toast.error(error.message);
                navigate('/screening-question');
            } finally {
                setLoading(false);
            };
        };
        
        fetchScreeningQuestion();
    }, [id, t, navigate]);
    
    const isDisabled = useMemo(() => {
        const isBasicFieldsValid = optionAID && optionBID && optionCID && optionDID && optionAEN && optionBEN && optionCEN && optionDEN;
        return !(isBasicFieldsValid);
    }, [optionAID, optionBID, optionCID, optionDID, optionAEN, optionBEN, optionCEN, optionDEN]);
    
    const handleUpdate = async () => {
        try {
            const data = {
                type,
                sequence: parseInt(sequence, 10),
                options: [
                    {
                        language: 'id',
                        option_a: optionAID,
                        option_b: optionBID,
                        option_c: optionCID,
                        option_d: optionDID,
                    },
                    {
                        language: 'en',
                        option_a: optionAEN,
                        option_b: optionBEN,
                        option_c: optionCEN,
                        option_d: optionDEN,
                    },
                ],
            };
            
            await updateScreeningQuestion(id, data);
            navigate('/screening-question');
            toast.success(t('screeningQuestion.edit.message'));
        } catch (error) {
            toast.error(error.message);
        };
    };
    
    const handleBack = () => {
        navigate('/screening-question');
    };
    
    if (loading) {
        return (
            <Skeleton className='flex h-36 w-full rounded-lg' />
        ); 
    };
    
    const handleSequenceChange = (e, field) => {
        let value = e.target.value.replace(/\D/g, '');
        if (value) {
            value = Math.max(1, Math.min(18, parseInt(value, 10)));
        };
        
        setSequence(value);
    };
    
    return (
        <div className='flex flex-col p-5 rounded-lg bg-white space-y-5'>
            {/* Type */}
            <div className='flex flex-grow items-center'>
                <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.type.title')}:</span>
                <select
                    className={`
                        w-full px-3 py-2 rounded bg-white border border-platinum text-xs md:text-sm
                        hover:outline-none hover:border-grayX11
                        focus:outline-none focus:border-blackOlive
                    `}
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                >
                    <option value='' disabled={type !== ''}>{t('common.type.placeholderSelect')}</option>
                    <option value='Main'>{t('common.type.main')}</option>
                    <option value='Negative'>{t('common.type.negative')}</option>
                </select>
            </div>
            
            {/* Sequence */}
            <div className='flex flex-grow items-center'>
                <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.sequence.title')}:</span>
                <input
                    className={`
                        w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                        hover:outline-none hover:border-grayX11
                        focus:outline-none focus:border-blackOlive
                    `}
                    type='text'
                    placeholder={t('common.sequence.placeholderText')}
                    value={sequence}
                    onChange={(e) => handleSequenceChange(e, 'sequence')}
                />
            </div>
            
            {/* Option A */}
            <div className='space-y-5'>
                <span className='text-base md:text-lg font-medium'>{t('common.option.a')}:</span>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.option.a')} ({t('languages.indonesian')}):</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.option.placeholderText')}
                        value={optionAID}
                        onChange={(e) => setOptionAID(e.target.value)} 
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.option.a')} ({t('languages.english')}):</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.option.placeholderText')}
                        value={optionAEN}
                        onChange={(e) => setOptionAEN(e.target.value)} 
                    />
                </div>
            </div>
            
            {/* Option B */}
            <div className='space-y-5'>
                <span className='text-base md:text-lg font-medium'>{t('common.option.b')}:</span>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.option.b')} ({t('languages.indonesian')}):</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.option.placeholderText')}
                        value={optionBID}
                        onChange={(e) => setOptionBID(e.target.value)} 
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.option.b')} ({t('languages.english')}):</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.option.placeholderText')}
                        value={optionBEN}
                        onChange={(e) => setOptionBEN(e.target.value)} 
                    />
                </div>
            </div>
            
            {/* Option C */}
            <div className='space-y-5'>
                <span className='text-base md:text-lg font-medium'>{t('common.option.c')}:</span>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.option.c')} ({t('languages.indonesian')}):</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.option.placeholderText')}
                        value={optionCID}
                        onChange={(e) => setOptionCID(e.target.value)} 
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.option.c')} ({t('languages.english')}):</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.option.placeholderText')}
                        value={optionCEN}
                        onChange={(e) => setOptionCEN(e.target.value)} 
                    />
                </div>
            </div>
            
            {/* Option D */}
            <div className='space-y-5'>
                <span className='text-base md:text-lg font-medium'>{t('common.option.d')}:</span>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.option.d')} ({t('languages.indonesian')}):</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.option.placeholderText')}
                        value={optionDID}
                        onChange={(e) => setOptionDID(e.target.value)} 
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.option.d')} ({t('languages.english')}):</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.option.placeholderText')}
                        value={optionDEN}
                        onChange={(e) => setOptionDEN(e.target.value)} 
                    />
                </div>
            </div>
            <div className='flex justify-center sm:justify-end space-x-5'>
                <ButtonSecondary className='h-full w-24 md:w-32 md:text-sm' onClick={handleBack}>{t('common.action.back')}</ButtonSecondary>
                <ButtonPrimary className='h-full w-24 md:w-32 md:text-sm' isDisabled={isDisabled} onClick={handleUpdate}>{t('common.action.edit')}</ButtonPrimary>
            </div>
        </div>
    );
};

export default EditModal;
